/**
 * Commercial project subphase slugs, labels, auto-advance rules, and phase-complete gating.
 */
import {
  COMMERCIAL_DOMAINS,
  connectionIncludes,
  departmentIncludes,
  departmentIncludesFiber,
  normalizeDepartments,
  normalizeMulti,
  PROJECT_SUMMARY_TYPE_OPTIONS,
} from "./commercialProjectSchema";
import { canCloseCommercialProjectAsAccountant } from "./commercialRoleAccess";
import { getViewerRoleType } from "./userMatrixStorage";
import { isNetworkManagerRoleType } from "./projectLandingTabAccess";

export const SUBPHASE_KEYS = {
  SALES: "sales_subphase",
  FINANCE: "finance_subphase",
  DRAFTING: "drafting_subphase",
  PERMITTING: "permitting_subphase",
  CONSTRUCTION: "construction_subphase",
  FIBER: "fiber_subphase",
  NETWORKING: "networking_subphase",
  ACCOUNTING: "accounting_subphase",
};

export const SUBPHASE_LABELS = {
  pending_ntp: "Pending NTP",
  pending_walkout: "Pending walkout",
  pending_numbers: "Pending numbers",
  ready_to_be_signed: "Ready to be signed",
  unassigned: "Unassigned",
  in_progress: "In progress",
  in_review: "In review",
  ready_to_submit: "Ready to submit",
  application_submitted: "Application submitted",
  pending_revisions: "Pending Revisions",
  permit_received: "Permit Received",
  ready_to_schedule: "Ready to schedule",
  scheduled: "Scheduled",
  pending_fiber: "Pending Fiber",
  ready_to_configure: "Ready to configure",
  pending_configuration: "Pending configuration",
  ready_to_schedule_v: "Ready to schedule (VoIP)",
  installation_scheduled: "Installation scheduled",
  pending_service_activation: "Pending service activation",
  ready_to_bill: "Ready to bill",
  ready_for_closeout: "Ready for closeout",
  closed: "Closed",
};

/** Valid subphase slugs per workflow phase (commercial projects). */
export const SUBPHASE_SLUGS_BY_PHASE = {
  Sales: ["pending_ntp"],
  Finance: ["pending_walkout", "pending_numbers", "ready_to_be_signed"],
  Drafting: ["unassigned", "in_progress", "in_review"],
  Permitting: ["ready_to_submit", "application_submitted", "pending_revisions", "permit_received"],
  Construction: ["ready_to_schedule", "scheduled", "in_progress"],
  Fiber: ["ready_to_schedule", "scheduled", "in_progress"],
  Networking: [
    "ready_to_configure",
    "pending_configuration",
    "ready_to_schedule",
    "ready_to_schedule_v",
    "installation_scheduled",
    "pending_service_activation",
  ],
  Accounting: ["ready_to_bill", "ready_for_closeout"],
};

/** Legacy human labels and aliases → slug. */
const LEGACY_TO_SLUG = {
  pending_ntp: "pending_ntp",
  "pending ntp": "pending_ntp",
  pending_walkout: "pending_walkout",
  "pending walkout": "pending_walkout",
  pending_numbers: "pending_numbers",
  "pending numbers": "pending_numbers",
  "pending number's": "pending_numbers",
  ready_to_be_signed: "ready_to_be_signed",
  "ready to be signed": "ready_to_be_signed",
  unassigned: "unassigned",
  in_progress: "in_progress",
  "in progress": "in_progress",
  in_review: "in_review",
  "in review": "in_review",
  ready_to_submit: "ready_to_submit",
  "ready to submit": "ready_to_submit",
  application_submitted: "application_submitted",
  "application submitted": "application_submitted",
  "app(s) submitted": "application_submitted",
  pending_revisions: "pending_revisions",
  "pending revisions": "pending_revisions",
  permit_received: "permit_received",
  "permit received": "permit_received",
  permit_issued: "permit_received",
  "permit issued": "permit_received",
  "construction permit issued": "permit_received",
  ready_to_schedule: "ready_to_schedule",
  "ready to schedule": "ready_to_schedule",
  scheduled: "scheduled",
  pending_fiber: "pending_fiber",
  "pending fiber": "pending_fiber",
  ready_to_configure: "ready_to_configure",
  "ready to configure": "ready_to_configure",
  assign: "ready_to_configure",
  pending_configuration: "pending_configuration",
  "pending configuration": "pending_configuration",
  ready_to_schedule_v: "ready_to_schedule_v",
  installation_scheduled: "installation_scheduled",
  "installation scheduled": "installation_scheduled",
  pending_service_activation: "pending_service_activation",
  "pending service activation": "pending_service_activation",
  ready_to_bill: "ready_to_bill",
  "ready to bill": "ready_to_bill",
  ready_for_closeout: "ready_for_closeout",
  "ready for closeout": "ready_for_closeout",
  closed: "closed",
  pending_assignment: "ready_to_configure",
  "pending construction": "ready_to_schedule",
};

const PHASE_TO_SUBPHASE_KEY = {
  sales: SUBPHASE_KEYS.SALES,
  finance: SUBPHASE_KEYS.FINANCE,
  drafting: SUBPHASE_KEYS.DRAFTING,
  engineering: SUBPHASE_KEYS.DRAFTING,
  permitting: SUBPHASE_KEYS.PERMITTING,
  construction: SUBPHASE_KEYS.CONSTRUCTION,
  fiber: SUBPHASE_KEYS.FIBER,
  networking: SUBPHASE_KEYS.NETWORKING,
  accounting: SUBPHASE_KEYS.ACCOUNTING,
};

/** Subphase slug at which the phase Complete button is enabled. */
export const PHASE_COMPLETE_READY_SLUG = {
  Sales: "pending_ntp",
  Finance: "ready_to_be_signed",
  Drafting: "in_review",
  Permitting: "permit_received",
  Construction: "in_progress",
  Networking: "pending_service_activation",
};

const MANUAL_ONLY_SLUGS = new Set([
  "scheduled",
  "in_progress",
  "pending_fiber",
  "installation_scheduled",
  "pending_revisions",
]);

const RANK_BY_KEY = {
  [SUBPHASE_KEYS.SALES]: { pending_ntp: 0 },
  [SUBPHASE_KEYS.FINANCE]: {
    pending_walkout: 0,
    pending_numbers: 1,
    ready_to_be_signed: 2,
  },
  [SUBPHASE_KEYS.DRAFTING]: { unassigned: 0, in_progress: 1, in_review: 2 },
  [SUBPHASE_KEYS.PERMITTING]: {
    ready_to_submit: 0,
    application_submitted: 1,
    pending_revisions: 2,
    permit_received: 3,
  },
  [SUBPHASE_KEYS.CONSTRUCTION]: {
    ready_to_schedule: 0,
    scheduled: 1,
    in_progress: 2,
    pending_fiber: 3,
  },
  [SUBPHASE_KEYS.FIBER]: { ready_to_schedule: 0, scheduled: 1, in_progress: 2 },
  [SUBPHASE_KEYS.NETWORKING]: {
    ready_to_configure: 0,
    pending_configuration: 1,
    ready_to_schedule: 2,
    ready_to_schedule_v: 3,
    installation_scheduled: 4,
    pending_service_activation: 5,
  },
  [SUBPHASE_KEYS.ACCOUNTING]: { ready_to_bill: 0, ready_for_closeout: 0, closed: 1 },
};

function slugRankForKey(subphaseKey, slug) {
  if (!slug || !subphaseKey) return -1;
  const normalized = normalizeSubphaseSlug(slug);
  return RANK_BY_KEY[subphaseKey]?.[normalized] ?? -1;
}

function readManualSubphaseOverrides(summary) {
  const raw = summary?.subphases_manual;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  return raw;
}

function pickHigherSlug(subphaseKey, existingSlug, computedSlug) {
  const a = normalizeSubphaseSlug(existingSlug);
  const b = normalizeSubphaseSlug(computedSlug);
  if (!a) return b;
  if (!b) return a;
  if (MANUAL_ONLY_SLUGS.has(a) && slugRankForKey(subphaseKey, a) >= slugRankForKey(subphaseKey, b)) {
    return a;
  }
  // Auto-advance only when evidence is strictly ahead. Equal rank keeps the stored
  // (often manual) choice — e.g. Accounting ready_to_bill vs ready_for_closeout.
  return slugRankForKey(subphaseKey, b) > slugRankForKey(subphaseKey, a) ? b : a;
}

/**
 * Merge evidence-derived slug with stored value.
 * Explicit UI edits set `summary.subphases_manual[key]` so they are not clobbered by the
 * evidence floor on save (users can move subphase down as well as up).
 */
function mergeManual(subphaseKey, existingSlug, computedSlug, manualOverrides) {
  const existing = normalizeSubphaseSlug(existingSlug);
  const computed = normalizeSubphaseSlug(computedSlug);
  if (manualOverrides?.[subphaseKey] && existing) {
    return existing;
  }
  if (
    MANUAL_ONLY_SLUGS.has(existing) &&
    slugRankForKey(subphaseKey, existing) > slugRankForKey(subphaseKey, computed)
  ) {
    return existing;
  }
  return pickHigherSlug(subphaseKey, existing, computed);
}

function isPlainObject(v) {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

export function normalizeSubphaseSlug(raw) {
  if (raw == null || raw === "") return "";
  const t = String(raw).trim();
  if (!t) return "";
  const lower = t.toLowerCase().replace(/\s+/g, " ");
  const underscored = lower.replace(/\s+/g, "_").replace(/['']/g, "");
  if (SUBPHASE_LABELS[underscored]) return underscored;
  if (LEGACY_TO_SLUG[underscored]) return LEGACY_TO_SLUG[underscored];
  if (LEGACY_TO_SLUG[lower]) return LEGACY_TO_SLUG[lower];
  return underscored;
}

export function formatSubphaseLabel(raw) {
  const slug = normalizeSubphaseSlug(raw);
  if (!slug) return raw != null ? String(raw).trim() : "";
  return SUBPHASE_LABELS[slug] || slug.replace(/_/g, " ");
}

export function subphaseKeyForPhaseLabel(phaseLabel) {
  if (phaseLabel == null || phaseLabel === "") return null;
  const n = String(phaseLabel).trim().toLowerCase().replace(/\s+/g, "_");
  return PHASE_TO_SUBPHASE_KEY[n] ?? null;
}

/** Dropdown options `{ value: slug, label }` for the active phase on project summary. */
export function subphaseSelectOptionsForPhase(phaseLabel, summary) {
  const phase = phaseLabel != null ? String(phaseLabel).trim() : "";
  if (!phase) return [];
  if (phase === "Sales" && isNonRevenueOrCapitalProject(summary)) return [];
  if (phase === "Finance" && isNonRevenueOrCapitalProject(summary)) return [];
  let slugs = SUBPHASE_SLUGS_BY_PHASE[phase] ? [...SUBPHASE_SLUGS_BY_PHASE[phase]] : [];
  if (phase === "Accounting") {
    if (isAccountingClosed(summary, null)) return [];
    if (isRevenueProject(summary)) slugs = ["ready_to_bill"];
    else if (isNonRevenueOrCapitalProject(summary)) slugs = ["ready_for_closeout"];
  }
  return slugs.map((slug) => ({
    value: slug,
    label: formatSubphaseLabel(slug),
  }));
}

/**
 * First/default subphase slug for entering a phase (Accounting respects revenue vs non-revenue).
 * Closed only when invoiced — Networking Complete must not land Accounting on Closed.
 * @param {string} phaseLabel
 * @param {object|null} summary
 * @param {object|null} domains
 */
export function defaultSubphaseSlugForPhase(phaseLabel, summary = null, domains = null) {
  const phase = phaseLabel != null ? String(phaseLabel).trim() : "";
  if (!phase) return "";
  if (phase === "Sales" && isNonRevenueOrCapitalProject(summary)) return "";
  if (phase === "Finance" && isNonRevenueOrCapitalProject(summary)) return "";
  if (phase === "Accounting") {
    if (hasInvoicedDate(domains)) return "closed";
    if (isRevenueProject(summary)) return "ready_to_bill";
    if (isNonRevenueOrCapitalProject(summary)) return "ready_for_closeout";
    return "ready_to_bill";
  }
  const slugs = SUBPHASE_SLUGS_BY_PHASE[phase];
  return slugs && slugs.length ? slugs[0] : "";
}

function slugIsValidForPhase(phaseLabel, slug) {
  const phase = phaseLabel != null ? String(phaseLabel).trim() : "";
  const normalized = normalizeSubphaseSlug(slug);
  if (!phase || !normalized) return false;
  if (phase === "Accounting" && normalized === "closed") return true;
  // Expansion-only Construction subphase (not in commercial SUBPHASE_SLUGS_BY_PHASE).
  if (phase === "Construction" && normalized === "pending_fiber") return true;
  const slugs = SUBPHASE_SLUGS_BY_PHASE[phase];
  return Array.isArray(slugs) && slugs.includes(normalized);
}

/** Keep legacy singular `subphase` aligned with the keyed value for `summary.phase`. */
export function syncSingularSubphase(summary) {
  if (!summary || typeof summary !== "object") return summary;
  const keyed = readSubphaseForCurrentPhase({ ...summary, subphase: "" });
  if (keyed) return { ...summary, subphase: keyed };
  return { ...summary, subphase: "" };
}

/**
 * On phase Complete: land on the next phase’s first/default subphase (not mid-ladder leftovers).
 * @param {object} summary
 * @param {string} nextPhaseLabel
 * @param {object|null} domains
 */
export function seedEnteredPhaseDefaultSubphase(summary, nextPhaseLabel, domains = null) {
  const base = clearManualSubphaseOverrides(
    summary && typeof summary === "object" ? { ...summary } : {}
  );
  const phase = nextPhaseLabel != null ? String(nextPhaseLabel).trim() : "";
  base.phase = phase;
  const key = subphaseKeyForPhaseLabel(phase);
  const defaultSlug = defaultSubphaseSlugForPhase(phase, base, domains);
  // Networking may leave status Complete; Accounting stays Active until accountant closes.
  if (phase === "Accounting" && !isAccountantClosedProject(base)) {
    base.status = "Active";
  }
  if (!key || !defaultSlug) {
    return syncSingularSubphase(base);
  }
  // Seeded defaults are auto, not sticky manual overrides.
  const seeded = applySubphaseEditForPhaseKey(base, key, defaultSlug);
  return syncSingularSubphase(clearManualSubphaseOverrides(seeded));
}

/**
 * Force entered-phase default inside acf_groups_json (server + client after recompute).
 * @param {object} acfGroupsJson
 * @param {string} phaseLabel
 */
export function forceEnteredPhaseDefaultSubphaseInAcf(acfGroupsJson, phaseLabel) {
  const base = isPlainObject(acfGroupsJson) ? JSON.parse(JSON.stringify(acfGroupsJson)) : {};
  const summaryKey = isPlainObject(base[COMMERCIAL_DOMAINS.SUMMARY])
    ? COMMERCIAL_DOMAINS.SUMMARY
    : "Project Summary Test 2";
  if (!isPlainObject(base[summaryKey])) base[summaryKey] = {};
  const domains = {
    [COMMERCIAL_DOMAINS.SUMMARY]: base[summaryKey],
    [COMMERCIAL_DOMAINS.ACCOUNTING]: base[COMMERCIAL_DOMAINS.ACCOUNTING],
  };
  const seeded = seedEnteredPhaseDefaultSubphase(base[summaryKey], phaseLabel, domains);
  base[summaryKey] = seeded;
  if (summaryKey !== COMMERCIAL_DOMAINS.SUMMARY && isPlainObject(base[COMMERCIAL_DOMAINS.SUMMARY])) {
    base[COMMERCIAL_DOMAINS.SUMMARY] = {
      ...base[COMMERCIAL_DOMAINS.SUMMARY],
      phase: seeded.phase,
      status: seeded.status,
      subphases: seeded.subphases,
      subphase: seeded.subphase,
    };
  }
  return base;
}

/** Current subphase slug for `summary.phase` from `summary.subphases`. */
export function readSubphaseForCurrentPhase(summary, domains = null) {
  if (!summary || typeof summary !== "object") return "";
  if (isAccountingClosed(summary, domains)) return "closed";
  const key = subphaseKeyForPhaseLabel(summary.phase);
  const subObj = summary.subphases;
  if (key && subObj && typeof subObj === "object" && subObj[key] != null && String(subObj[key]).trim() !== "") {
    return normalizeSubphaseSlug(subObj[key]);
  }
  // Only use legacy singular when it is valid for the *current* phase (avoid stale cross-phase labels).
  if (summary.subphase != null && String(summary.subphase).trim() !== "") {
    const singular = normalizeSubphaseSlug(summary.subphase);
    if (slugIsValidForPhase(summary.phase, singular)) return singular;
  }
  return "";
}

/** Merge a subphase slug edit into project summary (`subphases` + legacy `subphase`). */
export function applySubphaseEditToSummary(summary, slug) {
  const base = summary && typeof summary === "object" ? { ...summary } : {};
  const key = subphaseKeyForPhaseLabel(base.phase);
  return applySubphaseEditForPhaseKey(base, key, slug);
}

/** Update a specific workflow phase subphase (e.g. permitting while project is in Construction). */
export function applySubphaseEditForPhaseKey(summary, subphaseKey, slug) {
  const base = summary && typeof summary === "object" ? { ...summary } : {};
  const key = subphaseKey != null ? String(subphaseKey).trim() : "";
  const normalized = slug != null && String(slug).trim() !== "" ? normalizeSubphaseSlug(slug) : "";
  const subphases =
    base.subphases && typeof base.subphases === "object" && !Array.isArray(base.subphases)
      ? { ...base.subphases }
      : {};
  const manual = { ...readManualSubphaseOverrides(base) };
  if (key && normalized) {
    subphases[key] = normalized;
    manual[key] = true;
  } else if (key && !normalized) {
    delete subphases[key];
    delete manual[key];
  }
  const phaseKey = subphaseKeyForPhaseLabel(base.phase);
  return {
    ...base,
    subphases,
    subphases_manual: manual,
    subphase: phaseKey === key && normalized ? normalized : base.subphase || "",
  };
}

/** Drop sticky manual subphase flags (e.g. when the user changes Phase). */
export function clearManualSubphaseOverrides(summary) {
  if (!summary || typeof summary !== "object") return summary;
  if (!summary.subphases_manual) return summary;
  const next = { ...summary };
  delete next.subphases_manual;
  return next;
}

export function readSubphaseForPhaseKey(summary, subphaseKey) {
  const key = subphaseKey != null ? String(subphaseKey).trim() : "";
  if (!key) return "";
  const subphases = summary?.subphases;
  if (subphases && typeof subphases === "object" && !Array.isArray(subphases)) {
    const v = subphases[key];
    if (v != null && String(v).trim() !== "") return normalizeSubphaseSlug(v);
  }
  return "";
}

export function projectSummaryType(summary) {
  const t = summary?.type != null ? String(summary.type).trim() : "";
  if (!t) return "";
  const lower = t.toLowerCase();
  for (const opt of PROJECT_SUMMARY_TYPE_OPTIONS) {
    if (opt.toLowerCase() === lower) return opt;
  }
  return t;
}

export function isRevenueProject(summary) {
  return projectSummaryType(summary).toLowerCase() === "revenue";
}

export function isNonRevenueOrCapitalProject(summary) {
  const t = projectSummaryType(summary).toLowerCase();
  return t === "non-revenue" || t === "capital";
}

export function isRenewalSubtype(summary) {
  return normalizeMulti(summary?.subtype).some(
    (v) => String(v).trim().toLowerCase() === "renewal"
  );
}

export function isUpgradeSubtype(summary) {
  return normalizeMulti(summary?.subtype).some(
    (v) => String(v).trim().toLowerCase() === "upgrade"
  );
}

export function isSummaryStatusComplete(summary) {
  const s = summary?.status != null ? String(summary.status).trim().toLowerCase() : "";
  return s === "complete";
}

export function isAccountingPhase(summary) {
  const p = summary?.phase != null ? String(summary.phase).trim().toLowerCase() : "";
  return p === "accounting";
}

/** True when accounting_details.invoiced (or equivalent) has a date. */
export function hasInvoicedDate(domains) {
  const a = domains?.[COMMERCIAL_DOMAINS.ACCOUNTING];
  if (!a || typeof a !== "object") return false;
  return a.invoiced != null && String(a.invoiced).trim() !== "";
}

/**
 * Accounting Closed once an invoiced date is set.
 * Networking Complete must not close Accounting.
 */
export function isAccountingClosed(summary, domains = null) {
  if (!isAccountingPhase(summary)) return false;
  if (domains != null) return hasInvoicedDate(domains);
  // Domains omitted: fall back to stored Closed (post-invoice / accountant close).
  return normalizeSubphaseSlug(summary?.subphases?.[SUBPHASE_KEYS.ACCOUNTING]) === "closed";
}

/**
 * Whether a workflow phase applies given departments / renewal subtype.
 * Pure renewals skip ops phases (Sales/Finance/Accounting only). Renewal+Upgrade
 * still follows department involvement so Networking applies when selected.
 */
export function commercialPhaseApplies(phase, ctx) {
  const { departments, isRenewal, summary } = ctx || {};
  if (summary && isNonRevenueOrCapitalProject(summary)) {
    if (phase === "Sales" || phase === "Finance") return false;
  }
  if (isRenewal && !isUpgradeSubtype(summary)) {
    const acctOnly =
      departments.includes("Accounting") &&
      !departments.includes("Engineering") &&
      !departments.includes("Construction") &&
      !departmentIncludesFiber({ departments }) &&
      !departments.includes("Networking");
    if (acctOnly) {
      return phase === "Sales" || phase === "Accounting";
    }
    return phase === "Sales" || phase === "Finance" || phase === "Accounting";
  }
  if (phase === "Drafting" || phase === "Permitting") {
    return departments.includes("Engineering");
  }
  if (phase === "Construction") return departments.includes("Construction");
  if (phase === "Fiber") return departmentIncludesFiber({ departments });
  if (phase === "Networking") return departments.includes("Networking");
  return true;
}

export function commercialPhaseContext(domains) {
  const summary = domains?.[COMMERCIAL_DOMAINS.SUMMARY] || {};
  const departments = normalizeDepartments(summary.departments ?? summary.department_involvement);
  return {
    summary,
    departments,
    isRenewal: isRenewalSubtype(summary),
  };
}

/** True when a stored value counts as an uploaded file/URL. */
export function fieldHasUpload(value) {
  if (value == null || value === "") return false;
  if (typeof value === "string") {
    const t = value.trim();
    return t.length > 0;
  }
  if (Array.isArray(value)) {
    return value.some((item) => fieldHasUpload(item));
  }
  if (isPlainObject(value)) {
    if (fieldHasUpload(value.url)) return true;
    if (fieldHasUpload(value.file)) return true;
    if (fieldHasUpload(value.filename)) return true;
    return Object.values(value).some((v) => fieldHasUpload(v));
  }
  return false;
}

function repeaterRows(value) {
  if (Array.isArray(value)) return value;
  if (!isPlainObject(value)) return [];
  return Object.keys(value)
    .filter((k) => /^\d+$/.test(k))
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => value[k]);
}

function permitsHaveField(permits, fieldKey) {
  return repeaterRows(permits).some((row) => {
    if (fieldHasUpload(row?.[fieldKey])) return true;
    return repeaterRows(row?.riders).some((rider) => fieldHasUpload(rider?.[fieldKey]));
  });
}

function computeFinanceSubphase(existing, domains, overrides) {
  const summary = domains[COMMERCIAL_DOMAINS.SUMMARY] || {};
  if (isNonRevenueOrCapitalProject(summary)) return null;
  const eng = domains[COMMERCIAL_DOMAINS.ENGINEERING] || {};
  let computed = "pending_walkout";
  if (fieldHasUpload(eng.site_walk_files)) computed = "pending_numbers";
  if (fieldHasUpload(summary.signed_work_order)) computed = "ready_to_be_signed";
  return mergeManual(SUBPHASE_KEYS.FINANCE, existing, computed, overrides);
}

function computeDraftingSubphase(existing, domains, ctx, overrides) {
  if (!departmentIncludes(ctx.summary, "Engineering")) return null;
  const eng = domains[COMMERCIAL_DOMAINS.ENGINEERING] || {};
  let computed = "unassigned";
  if (eng.drafter != null && String(eng.drafter).trim() !== "") computed = "in_progress";
  if (permitsHaveField(eng.permits, "permit_plan")) computed = "in_review";
  return mergeManual(SUBPHASE_KEYS.DRAFTING, existing, computed, overrides);
}

function computePermittingSubphase(existing, domains, ctx, overrides) {
  if (!departmentIncludes(ctx.summary, "Engineering")) return null;
  const eng = domains[COMMERCIAL_DOMAINS.ENGINEERING] || {};
  let computed = "ready_to_submit";
  if (permitsHaveField(eng.permits, "permit_application")) computed = "application_submitted";
  if (permitsHaveField(eng.permits, "permit")) computed = "permit_received";
  return mergeManual(SUBPHASE_KEYS.PERMITTING, existing, computed, overrides);
}

function hasScheduledDate(value) {
  return value != null && String(value).trim() !== "";
}

function hasConstructionSchedule(domains) {
  const c = domains[COMMERCIAL_DOMAINS.CONSTRUCTION] || {};
  return hasScheduledDate(c.construction_start_date) && hasScheduledDate(c.construction_end_date);
}

function hasFiberSchedule(domains) {
  const f = domains[COMMERCIAL_DOMAINS.FIBER] || {};
  return (
    hasScheduledDate(f.splicing_start_date) &&
    hasScheduledDate(f.splicing_end_date) &&
    f.splicer != null &&
    String(f.splicer).trim() !== ""
  );
}

function hasText(value) {
  return value != null && String(value).trim() !== "";
}

function installationRowScheduled(row) {
  return (
    hasText(row?.location) &&
    hasText(row?.technician) &&
    hasScheduledDate(row?.start_date) &&
    hasScheduledDate(row?.end_date)
  );
}

export function hasNetworkingInstallationSchedule(domains) {
  const net = domains?.[COMMERCIAL_DOMAINS.NETWORKING] || {};
  return repeaterRows(net.installations).some(installationRowScheduled);
}

export function installationRowIsSchedulable(row) {
  return installationRowScheduled(row);
}

function computeConstructionSubphase(existing, domains, ctx, overrides) {
  if (!departmentIncludes(ctx.summary, "Construction")) return null;
  let computed = "ready_to_schedule";
  if (hasConstructionSchedule(domains)) computed = "scheduled";
  return mergeManual(SUBPHASE_KEYS.CONSTRUCTION, existing, computed, overrides);
}

function computeFiberSubphase(existing, domains, ctx, overrides) {
  if (!departmentIncludesFiber(ctx.summary)) return null;
  let computed = "ready_to_schedule";
  if (hasFiberSchedule(domains)) computed = "scheduled";
  return mergeManual(SUBPHASE_KEYS.FIBER, existing, computed, overrides);
}

function readNetworkingCutSheet(domains, legacyAcf) {
  const net = domains[COMMERCIAL_DOMAINS.NETWORKING] || {};
  if (fieldHasUpload(net.cut_sheet)) return true;
  const legacyNet = legacyAcf?.["Networking Test 2"] ?? legacyAcf?.networking_test_2;
  return fieldHasUpload(legacyNet?.cut_sheet);
}

function hasAssignedNetworkingTechnician(domains) {
  const net = domains?.[COMMERCIAL_DOMAINS.NETWORKING] || {};
  // Legacy top-level field (removed from UI; still honor if present).
  if (hasText(net.assigned_technician)) return true;
  return repeaterRows(net.installations).some((row) => hasText(row?.technician));
}

function computeNetworkingSubphase(existing, domains, ctx, legacyAcf, overrides) {
  if (!departmentIncludes(ctx.summary, "Networking")) return null;
  const net = domains[COMMERCIAL_DOMAINS.NETWORKING] || {};
  let computed = "ready_to_configure";
  if (hasAssignedNetworkingTechnician(domains)) {
    computed = "pending_configuration";
  }
  const hasTopology = fieldHasUpload(net.topology);
  const hasNid = fieldHasUpload(net.nid_config);
  if (hasTopology && hasNid) {
    computed = connectionIncludes(net, ["VoIP"]) ? "ready_to_schedule_v" : "ready_to_schedule";
  }
  if (hasNetworkingInstallationSchedule(domains)) {
    computed = "installation_scheduled";
  }
  if (readNetworkingCutSheet(domains, legacyAcf)) {
    computed = "pending_service_activation";
  }
  return mergeManual(SUBPHASE_KEYS.NETWORKING, existing, computed, overrides);
}

function computeAccountingSubphase(existing, ctx, domains, overrides) {
  if (hasInvoicedDate(domains)) return "closed";
  let computed = "ready_to_bill";
  if (isRevenueProject(ctx.summary)) computed = "ready_to_bill";
  else if (isNonRevenueOrCapitalProject(ctx.summary)) computed = "ready_for_closeout";
  // Closed requires an invoiced date — do not keep a premature Closed from Networking Complete.
  const existingSlug = normalizeSubphaseSlug(existing);
  if (existingSlug === "closed") return computed;
  return mergeManual(SUBPHASE_KEYS.ACCOUNTING, existing, computed, overrides);
}

function computeSalesSubphase(existing, ctx) {
  if (isNonRevenueOrCapitalProject(ctx.summary)) return null;
  return "pending_ntp";
}

export function defaultCommercialSubphases(domains) {
  const ctx = commercialPhaseContext(domains);
  const out = {};
  const set = (key, slug) => {
    if (slug != null) out[key] = slug;
  };
  set(SUBPHASE_KEYS.SALES, computeSalesSubphase(null, ctx));
  set(SUBPHASE_KEYS.FINANCE, computeFinanceSubphase(null, domains));
  set(SUBPHASE_KEYS.DRAFTING, computeDraftingSubphase(null, domains, ctx));
  set(SUBPHASE_KEYS.PERMITTING, computePermittingSubphase(null, domains, ctx));
  set(SUBPHASE_KEYS.CONSTRUCTION, computeConstructionSubphase(null, domains, ctx));
  set(SUBPHASE_KEYS.FIBER, computeFiberSubphase(null, domains, ctx));
  set(SUBPHASE_KEYS.NETWORKING, computeNetworkingSubphase(null, domains, ctx, null));
  set(SUBPHASE_KEYS.ACCOUNTING, computeAccountingSubphase(null, ctx, domains));
  return out;
}

/**
 * Recompute commercial subphases from domain state; preserves manual-only advances
 * and sticky UI overrides (`summary.subphases_manual`).
 * @param {object} domains commercial domains from readCommercialDomains
 * @param {object|null} existingSubphases prior summary.subphases
 * @param {object|null} legacyAcf optional full acf_groups_json for legacy cut_sheet
 */
export function computeCommercialSubphases(domains, existingSubphases, legacyAcf = null) {
  const ctx = commercialPhaseContext(domains);
  const existing =
    existingSubphases && typeof existingSubphases === "object" && !Array.isArray(existingSubphases)
      ? existingSubphases
      : {};
  const overrides = {
    ...readManualSubphaseOverrides(ctx.summary),
    ...readManualSubphaseOverrides(
      legacyAcf && typeof legacyAcf === "object" ? legacyAcf[COMMERCIAL_DOMAINS.SUMMARY] : null
    ),
  };
  // Start from existing so keys skipped by department/type gates (compute returns null)
  // are not wiped on every save — that was dropping manual subphase edits.
  const out = { ...existing };
  const set = (key, slug) => {
    if (slug != null) out[key] = slug;
  };
  set(
    SUBPHASE_KEYS.SALES,
    computeSalesSubphase(existing[SUBPHASE_KEYS.SALES], ctx)
  );
  set(
    SUBPHASE_KEYS.FINANCE,
    computeFinanceSubphase(existing[SUBPHASE_KEYS.FINANCE], domains, overrides)
  );
  set(
    SUBPHASE_KEYS.DRAFTING,
    computeDraftingSubphase(existing[SUBPHASE_KEYS.DRAFTING], domains, ctx, overrides)
  );
  set(
    SUBPHASE_KEYS.PERMITTING,
    computePermittingSubphase(existing[SUBPHASE_KEYS.PERMITTING], domains, ctx, overrides)
  );
  set(
    SUBPHASE_KEYS.CONSTRUCTION,
    computeConstructionSubphase(existing[SUBPHASE_KEYS.CONSTRUCTION], domains, ctx, overrides)
  );
  set(
    SUBPHASE_KEYS.FIBER,
    computeFiberSubphase(existing[SUBPHASE_KEYS.FIBER], domains, ctx, overrides)
  );
  set(
    SUBPHASE_KEYS.NETWORKING,
    computeNetworkingSubphase(
      existing[SUBPHASE_KEYS.NETWORKING],
      domains,
      ctx,
      legacyAcf,
      overrides
    )
  );
  set(
    SUBPHASE_KEYS.ACCOUNTING,
    computeAccountingSubphase(existing[SUBPHASE_KEYS.ACCOUNTING], ctx, domains, overrides)
  );
  return out;
}

export function subphaseSlugForPhase(domains, phaseLabel, existingSubphases, legacyAcf) {
  const key = subphaseKeyForPhaseLabel(phaseLabel);
  if (!key) return "";
  const computed = computeCommercialSubphases(domains, existingSubphases, legacyAcf);
  return normalizeSubphaseSlug(computed[key]);
}

export function networkingCompleteUpdatesStatus(domains, existingSubphases, legacyAcf) {
  const summary = domains?.[COMMERCIAL_DOMAINS.SUMMARY] || {};
  const phase = summary?.phase != null ? String(summary.phase).trim() : "";
  // Cut sheets / networking evidence can exist before Networking; phase-complete gating only.
  if (phase !== "Networking") return false;
  const slug = subphaseSlugForPhase(
    domains,
    "Networking",
    existingSubphases,
    legacyAcf
  );
  return slug === "pending_service_activation";
}

/** True when an accountant has closed the project (status Complete is reserved for this). */
export function isAccountantClosedProject(summary) {
  const v = summary?.accountant_closed;
  return v === true || v === 1 || v === "1" || String(v).trim().toLowerCase() === "true";
}

/**
 * Complete status is only set by accountant Close Project.
 * Clear Networking/invoiced auto-Complete leftovers; preserve after accountant closeout.
 */
export function reconcileCommercialStatusForPhase(summary, domains = null) {
  if (!summary || typeof summary !== "object") return summary;
  const status = summary.status != null ? String(summary.status).trim() : "";
  if (status.toLowerCase() !== "complete") return summary;
  if (isAccountantClosedProject(summary) && isAccountingPhase(summary)) return summary;
  return { ...summary, status: "Active" };
}

export function needsConstructionScheduleEntry(summary, domains) {
  const phase = summary?.phase != null ? String(summary.phase).trim() : "";
  if (phase !== "Construction") return false;
  const slug = readSubphaseForCurrentPhase(summary);
  if (slug !== "ready_to_schedule") return false;
  return !hasConstructionSchedule(domains);
}

export function needsFiberScheduleEntry(summary, domains) {
  const phase = summary?.phase != null ? String(summary.phase).trim() : "";
  if (phase !== "Fiber") return false;
  const slug = readSubphaseForCurrentPhase(summary);
  if (slug !== "ready_to_schedule") return false;
  return !hasFiberSchedule(domains);
}

export function needsNetworkingScheduleEntry(summary, domains) {
  const phase = summary?.phase != null ? String(summary.phase).trim() : "";
  if (phase !== "Networking") return false;
  const slug = readSubphaseForCurrentPhase(summary);
  if (slug !== "ready_to_schedule" && slug !== "ready_to_schedule_v") return false;
  return !hasNetworkingInstallationSchedule(domains);
}

/** At least one fiber_files repeater row has a DLR file uploaded. */
export function hasFiberDlrUpload(domains) {
  const fib = domains?.[COMMERCIAL_DOMAINS.FIBER] || {};
  return repeaterRows(fib.fiber_files).some((row) => fieldHasUpload(row?.dlr));
}

/** Construction tab has at least one as-built PDF uploaded. */
export function hasConstructionAsbuiltUpload(domains) {
  const c = domains?.[COMMERCIAL_DOMAINS.CONSTRUCTION] || {};
  return fieldHasUpload(c.asbuilt);
}

/**
 * Whether the landing-page phase Complete button should show for the current phase.
 * Network Managers do not advance Finance (they complete Networking only).
 */
export function canShowPhaseCompleteButton(row, domains, phaseLabel, nextPhaseLabel, viewerEmail) {
  if (!phaseLabel || !nextPhaseLabel) return false;
  const ctx = commercialPhaseContext(domains);
  if (!commercialPhaseApplies(phaseLabel, ctx)) return false;

  if (phaseLabel === "Finance" && isNetworkManagerRoleType(getViewerRoleType(viewerEmail))) {
    return false;
  }

  const summary = domains?.[COMMERCIAL_DOMAINS.SUMMARY] || {};
  const existing = summary.subphases;

  // Fiber Complete depends only on a fiber run that has a DLR file.
  if (phaseLabel === "Fiber") {
    return hasFiberDlrUpload(domains);
  }

  // TEMPORARY: Construction complete no longer requires as-built uploads or a ready
  // subphase (in_progress). Any Construction-phase project can advance when next phase exists.
  if (phaseLabel === "Construction") {
    return true;
  }

  const slug = subphaseSlugForPhase(domains, phaseLabel, existing, row?.acf_groups_json);
  const readySlug = PHASE_COMPLETE_READY_SLUG[phaseLabel];
  if (!readySlug || slug !== readySlug) return false;

  if (phaseLabel === "Sales" && isNonRevenueOrCapitalProject(summary)) return false;
  if (phaseLabel === "Finance" && isNonRevenueOrCapitalProject(summary)) return false;
  return true;
}

/** PATCH body: mark a commercial project closed (Accounting / Complete / Closed). */
export function buildAccountantCloseCommercialProjectPatch(row) {
  const ag =
    row?.acf_groups_json != null &&
    typeof row.acf_groups_json === "object" &&
    !Array.isArray(row.acf_groups_json)
      ? JSON.parse(JSON.stringify(row.acf_groups_json))
      : {};
  const summaryKey = isPlainObject(ag[COMMERCIAL_DOMAINS.SUMMARY])
    ? COMMERCIAL_DOMAINS.SUMMARY
    : "Project Summary Test 2";
  if (!isPlainObject(ag[summaryKey])) ag[summaryKey] = {};
  // Closed persists through recompute only when invoiced is set.
  if (!isPlainObject(ag[COMMERCIAL_DOMAINS.ACCOUNTING])) ag[COMMERCIAL_DOMAINS.ACCOUNTING] = {};
  const acct = { ...ag[COMMERCIAL_DOMAINS.ACCOUNTING] };
  if (acct.invoiced == null || String(acct.invoiced).trim() === "") {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    acct.invoiced = `${yyyy}-${mm}-${dd}`;
  }
  ag[COMMERCIAL_DOMAINS.ACCOUNTING] = acct;
  let summary = {
    ...ag[summaryKey],
    phase: "Accounting",
    status: "Complete",
    accountant_closed: true,
  };
  summary = applySubphaseEditForPhaseKey(summary, SUBPHASE_KEYS.ACCOUNTING, "closed");
  ag[summaryKey] = summary;
  if (summaryKey !== COMMERCIAL_DOMAINS.SUMMARY && isPlainObject(ag[COMMERCIAL_DOMAINS.SUMMARY])) {
    ag[COMMERCIAL_DOMAINS.SUMMARY] = {
      ...ag[COMMERCIAL_DOMAINS.SUMMARY],
      phase: summary.phase,
      status: summary.status,
      accountant_closed: true,
      subphases: summary.subphases,
      subphase: summary.subphase,
    };
  }
  return {
    acf_groups_json: ag,
    status: "Complete",
    phase_label: "Accounting",
    invoiced_date: acct.invoiced,
    _source: "accountant-close-project",
  };
}

export function canShowAccountantCloseProjectButton(domains, viewerEmail) {
  if (!canCloseCommercialProjectAsAccountant(viewerEmail)) return false;
  const summary = domains?.[COMMERCIAL_DOMAINS.SUMMARY] || {};
  if (isAccountantClosedProject(summary) || isSummaryStatusComplete(summary)) return false;
  const phase = summary?.phase != null ? String(summary.phase).trim() : "";
  return phase.toLowerCase() === "accounting";
}

/** Merge computed subphases into acf_groups_json project_summary; status Complete only via accountant close. */
export function applyCommercialSubphasesToAcf(acfGroupsJson, domains) {
  const base = isPlainObject(acfGroupsJson) ? JSON.parse(JSON.stringify(acfGroupsJson)) : {};
  const summaryKey = isPlainObject(base[COMMERCIAL_DOMAINS.SUMMARY])
    ? COMMERCIAL_DOMAINS.SUMMARY
    : "Project Summary Test 2";
  if (!isPlainObject(base[summaryKey])) base[summaryKey] = {};
  let summary = base[summaryKey];
  const subphases = computeCommercialSubphases(domains, summary.subphases, base);
  summary = { ...summary, subphases };
  // Never auto-set Complete (Networking / invoiced); only accountant Close Project does.
  summary = reconcileCommercialStatusForPhase(summary, domains);
  summary = syncSingularSubphase(summary);
  base[summaryKey] = summary;
  if (summaryKey !== COMMERCIAL_DOMAINS.SUMMARY && isPlainObject(base[COMMERCIAL_DOMAINS.SUMMARY])) {
    base[COMMERCIAL_DOMAINS.SUMMARY].subphases = subphases;
    base[COMMERCIAL_DOMAINS.SUMMARY].subphase = summary.subphase;
    base[COMMERCIAL_DOMAINS.SUMMARY].status = summary.status;
  }
  return base;
}
